import { wmsApi, ecommerceApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface TransferOrderRequest {
  ecommerceOrderId: string;
  warehouseId: string;
  priority?: number;
}

export interface BulkTransferRequest {
  ecommerceOrderIds: string[];
  warehouseId: string;
  priority?: number;
}

export interface TransferResult {
  orderId: string;
  success: boolean;
  wmsOrderId?: string;
  wmsOrderNumber?: string;
  error?: string;
  warnings?: string[];
}

export interface TransferStatus {
  ecommerceOrderId: string;
  isTransferred: boolean;
  wmsOrderId?: string;
  wmsOrderNumber?: string;
  wmsStatus?: string;
}

export interface ProductMatchResult {
  ecomItemName: string;
  ecomSKU?: string;
  matchedProductId?: string;
  matchedProductName?: string;
  matchedBarcode?: string;
  matchMethod: string;
  isMatched: boolean;
}

export interface TransferPreview {
  ecommerceOrderId: string;
  orderNumber: string;
  customerName: string;
  platform?: string;
  itemCount: number;
  productMatches: ProductMatchResult[];
  matchedCount: number;
  unmatchedCount: number;
}

export interface AutoTransferSettings {
  warehouseId: string;
  enabled: boolean;
  triggerStatus: string;
  defaultPriority: number;
  /** null = eski kayıt (tüm kanallar). Dizi = kaydedilmiş seçim (boş = hiçbiri). */
  enabledChannels?: string[] | null;
}

export const orderTransferService = {
  async transfer(dto: TransferOrderRequest): Promise<{ success: boolean; data?: TransferResult; message?: string }> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_TRANSFER, dto);
    return resp.data;
  },

  async transferBulk(dto: BulkTransferRequest): Promise<{
    success: boolean;
    data: TransferResult[];
    totalTransferred: number;
    totalFailed: number;
  }> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_TRANSFER_BULK, dto);
    return resp.data;
  },

  async getTransferStatus(ecomOrderId: string): Promise<TransferStatus> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_TRANSFER_STATUS(ecomOrderId));
    return resp.data;
  },

  async getTransferStatusBulk(ecomOrderIds: string[]): Promise<TransferStatus[]> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_TRANSFER_STATUS_BULK, ecomOrderIds);
    return resp.data?.data ?? [];
  },

  async preview(dto: TransferOrderRequest): Promise<TransferPreview> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_TRANSFER_PREVIEW, dto);
    return resp.data;
  },

  async getAutoTransferSettings(warehouseId: string): Promise<AutoTransferSettings> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_AUTO_TRANSFER_SETTINGS(warehouseId));
    const data = resp.data?.data ?? resp.data;
    return {
      warehouseId,
      enabled: data?.enabled ?? data?.Enabled ?? false,
      triggerStatus: data?.triggerStatus ?? data?.TriggerStatus ?? 'Confirmed',
      defaultPriority: data?.defaultPriority ?? data?.DefaultPriority ?? 0,
      enabledChannels: data?.enabledChannels ?? data?.EnabledChannels ?? null,
    };
  },

  async updateAutoTransferSettings(dto: AutoTransferSettings): Promise<void> {
    // Alan adlarını açık gönder — kanal listesi asla düşmesin (toplama filtresi buradan okunur).
    await wmsApi.post(ACCOUNTING_API.WMS_AUTO_TRANSFER_UPDATE, {
      warehouseId: dto.warehouseId,
      enabled: dto.enabled,
      triggerStatus: dto.triggerStatus,
      defaultPriority: dto.defaultPriority,
      enabledChannels: Array.isArray(dto.enabledChannels) ? dto.enabledChannels : [],
    });
  },

  async bulkRetryAutoTransfer(): Promise<{
    success: boolean;
    transferred: number;
    skipped: number;
    failed: number;
    total: number;
    alreadyInWms: number;
    failedOrders: {
      orderId: string;
      orderNumber: string;
      marketplaceSource?: string;
      reason: string;
    }[];
  }> {
    const resp = await ecommerceApi.post(ACCOUNTING_API.ECOMMERCE_ORDERS_BULK_RETRY_WMS);
    return resp.data;
  },
};
